import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { FaCheckCircle, FaExclamationTriangle } from 'react-icons/fa';
import Modal from '../../common/Modal';
import Spinner from '../../common/Spinner';
import { activarProductoMaster } from '../../../services/activarProductoMaster.service';
import {
  getEstadoProductoMaster,
  getMensajeActivacionProducto,
  isProductoMasterImportado
} from '../../../utils/masterCatalogProduct';

const focusInput = (ref) => {
  window.requestAnimationFrame(() => {
    window.requestAnimationFrame(() => {
      const el = ref.current;
      if (!el) return;
      try {
        el.focus({ preventScroll: true });
      } catch {
        el.focus();
      }
      if (typeof el.select === 'function') el.select();
    });
  });
};

const fieldClass =
  'nexo-field w-full rounded-xl border-2 border-slate-200 bg-white text-slate-900 ' +
  'placeholder:text-slate-400 focus:border-e0e3-accent focus:outline-none focus:ring-4 focus:ring-e0e3-accent/25';

export default function ActivarProductoMasterModal({
  isOpen,
  onClose,
  producto,
  sucursalId,
  orgId,
  onSuccess,
  variant = 'default',
  onFocusScan
}) {
  const [precioVenta, setPrecioVenta] = useState('');
  const [cargarStock, setCargarStock] = useState(true);
  const [cantidad, setCantidad] = useState('10');
  const [guardando, setGuardando] = useState(false);
  const [resultado, setResultado] = useState(null);
  const precioRef = useRef(null);
  const cantidadRef = useRef(null);
  const isPos = variant === 'pos';

  useEffect(() => {
    if (!isOpen || !producto) return;
    setPrecioVenta('');
    setCargarStock(true);
    setCantidad('10');
    setResultado(null);
    setGuardando(false);
    const t = setTimeout(() => focusInput(precioRef), 120);
    return () => clearTimeout(t);
  }, [isOpen, producto?.id]);

  if (!producto) return null;

  const cerrarRapido = () => {
    setResultado(null);
    onClose?.();
    setTimeout(() => onFocusScan?.(), 80);
  };

  const accionPrincipal = () => (isPos ? 'vender' : 'continuar');

  const handleGuardar = async (accion = accionPrincipal()) => {
    const precio = parseFloat(String(precioVenta).replace(',', '.'));
    if (Number.isNaN(precio) || precio <= 0) {
      toast.error('Ingresá un precio de venta mayor a 0');
      focusInput(precioRef);
      return;
    }
    if (!sucursalId) {
      toast.error('Seleccioná una sucursal antes de activar el producto');
      return;
    }
    if (cargarStock) {
      const qty = parseInt(cantidad, 10);
      if (Number.isNaN(qty) || qty < 0) {
        toast.error('La cantidad de stock debe ser un número válido');
        focusInput(cantidadRef);
        return;
      }
    }

    try {
      setGuardando(true);
      const updated = await activarProductoMaster({
        productoId: producto.id,
        precioVenta: precio,
        sucursalId,
        orgId,
        cargarStock,
        cantidadStock: cargarStock ? parseInt(cantidad, 10) : 0
      });
      const estado = getEstadoProductoMaster(updated, updated.stock_sucursal);
      const mensaje = getMensajeActivacionProducto(estado);

      if (isPos) {
        if (estado === 'listo') toast.success(mensaje);
        else if (estado === 'sin_stock') toast.info(mensaje);
        onSuccess?.(updated, estado, { accion });
        cerrarRapido();
        return;
      }

      setResultado({ estado, mensaje, producto: updated });
      onSuccess?.(updated, estado, { accion });
      if (estado === 'listo') toast.success(mensaje);
      else if (estado === 'sin_stock') toast.info(mensaje);
    } catch (err) {
      console.error('[ActivarProductoMaster]', err);
      toast.error(err?.message || 'No se pudo activar el producto');
    } finally {
      setGuardando(false);
    }
  };

  const handleCerrar = () => {
    if (isPos) {
      cerrarRapido();
      return;
    }
    setResultado(null);
    onClose?.();
  };

  const handlePrecioKeyDown = (e) => {
    if (e.key !== 'Enter' || guardando) return;
    e.preventDefault();
    e.stopPropagation();
    if (cargarStock) {
      focusInput(cantidadRef);
      return;
    }
    handleGuardar(accionPrincipal());
  };

  const handleStockKeyDown = (e) => {
    if (e.key !== 'Enter' || guardando) return;
    e.preventDefault();
    e.stopPropagation();
    handleGuardar(accionPrincipal());
  };

  const titulo = resultado
    ? (resultado.estado === 'listo' ? 'Producto activado' : 'Precio cargado')
    : (isPos ? 'Activar para vender' : 'Activar producto');

  const btnPrimary =
    'w-full min-h-[48px] rounded-xl font-bold text-base text-white shadow-lg ' +
    'bg-e0e3-accent hover:bg-[#1c80e6] focus:outline-none focus:ring-4 focus:ring-e0e3-accent/40 ' +
    'disabled:opacity-60 disabled:cursor-not-allowed transition-colors';

  const btnSecondary =
    'w-full min-h-[48px] rounded-xl font-semibold text-base ' +
    'border-2 border-slate-300 bg-white text-slate-800 shadow-sm ' +
    'hover:bg-slate-50 hover:border-slate-400 focus:outline-none focus:ring-4 focus:ring-slate-300/50 ' +
    'disabled:opacity-60 disabled:cursor-not-allowed transition-colors';

  return (
    <Modal isOpen={isOpen} onClose={handleCerrar} title={titulo} size="sm" closeOnOverlay={!guardando}>
      {resultado && !isPos ? (
        <div className="space-y-4 py-2 text-center">
          {resultado.estado === 'listo' ? (
            <FaCheckCircle className="mx-auto text-5xl text-green-500" aria-hidden />
          ) : (
            <FaExclamationTriangle className="mx-auto text-5xl text-yellow-500" aria-hidden />
          )}
          <p className="text-lg font-semibold text-slate-900">{resultado.mensaje}</p>
          <p className="text-sm text-slate-600">{producto.nombre}</p>
          {producto.codigo_barras && (
            <p className="text-xs text-slate-500 font-mono">{producto.codigo_barras}</p>
          )}
          <button type="button" className={btnPrimary} onClick={handleCerrar}>
            Continuar
          </button>
        </div>
      ) : (
        <div
          className="space-y-4 rounded-xl bg-slate-50/80 p-1"
          data-activacion-master-modal="1"
          data-pos-busqueda-pausada="1"
          onKeyDownCapture={(e) => {
            if (e.key === 'Enter') e.stopPropagation();
          }}
        >
          <div className="rounded-xl border-2 border-orange-300 bg-orange-50 px-3 py-3 text-sm text-orange-950 shadow-sm">
            <p className="text-xs font-bold uppercase tracking-wide text-orange-800">Producto</p>
            <p className="font-semibold text-base mt-1 text-slate-900">{producto.nombre}</p>
            {isProductoMasterImportado(producto) && !isPos && (
              <p className="text-xs mt-1 text-orange-800">Catálogo importado — pendiente de activación</p>
            )}
            {producto.codigo_barras && (
              <p className="text-xs mt-1 font-mono text-slate-700">{producto.codigo_barras}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-bold text-slate-800 mb-1.5">
              Precio de venta *
            </label>
            <div className="relative">
              <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-slate-600 font-semibold">$</span>
              <input
                ref={precioRef}
                type="number"
                min="0"
                step="0.01"
                className={`${fieldClass} pl-8 text-lg font-semibold`}
                value={precioVenta}
                onChange={(e) => setPrecioVenta(e.target.value)}
                onKeyDown={handlePrecioKeyDown}
                placeholder="0.00"
                autoFocus
              />
            </div>
          </div>

          <div className="rounded-xl border-2 border-slate-200 bg-white p-3 space-y-3 shadow-sm">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-slate-400 text-e0e3-accent focus:ring-e0e3-accent"
                checked={cargarStock}
                onChange={(e) => setCargarStock(e.target.checked)}
              />
              <span className="text-sm font-bold text-slate-800">Cargar stock inicial</span>
            </label>
            {cargarStock && (
              <div>
                <label className="block text-sm font-bold text-slate-800 mb-1.5">Cantidad</label>
                <input
                  ref={cantidadRef}
                  type="number"
                  min="0"
                  step="1"
                  className={`${fieldClass} text-lg font-semibold`}
                  value={cantidad}
                  onChange={(e) => setCantidad(e.target.value)}
                  onKeyDown={handleStockKeyDown}
                />
                {!sucursalId && (
                  <p className="text-xs text-orange-800 mt-1 font-medium">Seleccioná sucursal en el encabezado para asignar stock.</p>
                )}
              </div>
            )}
          </div>

          {isPos ? (
            <div className="flex flex-col gap-3 pt-1 pb-1">
              <button
                type="button"
                className={btnPrimary}
                onClick={() => handleGuardar('vender')}
                disabled={guardando}
              >
                {guardando ? <Spinner size="sm" /> : 'Guardar y vender'}
              </button>
              <button
                type="button"
                className={btnSecondary}
                onClick={() => handleGuardar('continuar')}
                disabled={guardando}
              >
                Guardar y continuar
              </button>
              <p className="text-center text-xs font-medium text-slate-600">
                Enter en precio → stock · Enter en stock → guardar y vender
              </p>
            </div>
          ) : (
            <div className="flex gap-3 pt-1 pb-1">
              <button type="button" className={`${btnSecondary} flex-1`} onClick={handleCerrar} disabled={guardando}>
                Cancelar
              </button>
              <button type="button" className={`${btnPrimary} flex-1`} onClick={() => handleGuardar('continuar')} disabled={guardando}>
                {guardando ? <Spinner size="sm" /> : 'Guardar'}
              </button>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}
